import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID, IDPrefixes } from 'shared/types/id';
import { untaggedId } from 'shared/types/orderable-section';
import { RelationshipUpdate } from 'shared/types/relationship';
import { Task } from 'shared/types/task';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

import { useTagTree } from '../../hooks/use-tag-tree';
import { GroupedListProps } from '../../types';
import { TagHeader } from '../tag-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './tag-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const TagGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  tags,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
}) => {
  const today = useToday();
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useTagTree({
    tasks,
    tags,
    relationships,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const oldTreeData = useMemo(() => structuredClone(treeData), [treeData]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];
    const relationshipUpdates: RelationshipUpdate[] = [];

    // Process each task to detect section changes
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }

        // Find which section this task is now in
        const newSectionItem = updatedItems.find((section) =>
          section.children?.includes(item.id),
        );
        const oldSectionItem = Object.values(oldTreeData).find((section) =>
          section.children?.includes(item.id),
        );

        if (!newSectionItem || !oldSectionItem) {
          return;
        }

        const newSectionId = newSectionItem.id;
        const oldSectionId = oldSectionItem.id;

        // Skip if task hasn't moved between sections
        if (newSectionId === oldSectionId) {
          return;
        }

        const taskPrefixedId = createPrefixId(item.id, IDPrefixes.Task);

        // Get current tag relationships for this task to preserve other tags
        const relatedIds = relationshipsMap[taskPrefixedId] ?? [];
        const currentTagIds = relatedIds
          .filter((id) => getPrefixedIdPrefix(id) === IDPrefixes.Tag)
          .map((id) => getPrefixedId(id));

        // Remove old tag relationship (only if it exists and not untagged)
        if (
          oldSectionId !== untaggedId &&
          currentTagIds.includes(oldSectionId)
        ) {
          const oldTagPrefixedId = createPrefixId(oldSectionId, IDPrefixes.Tag);
          relationshipUpdates.push({
            id: oldTagPrefixedId,
            remove: [taskPrefixedId],
          });
        }

        // Add new tag relationship (only if it doesn't already exist and not untagged)
        if (
          newSectionId !== untaggedId &&
          !currentTagIds.includes(newSectionId)
        ) {
          const newTagPrefixedId = createPrefixId(newSectionId, IDPrefixes.Tag);
          relationshipUpdates.push({
            id: newTagPrefixedId,
            add: [taskPrefixedId],
          });
        }
      }

      // Collect section updates for custom order
      if (item.type === TreeItemTypes.Tag) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children
            ? item.children.filter((childId) => !childId.endsWith('-new-task'))
            : [],
        });
      }
    });

    // Pass relationship updates to onReorder callback
    onReorder(
      updatedSections,
      [],
      relationshipUpdates.length ? relationshipUpdates : undefined,
    );
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const tagsRef = useRef(tags);
  tagsRef.current = tags;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TreeItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TreeItemTypes.NewTask) {
          const tagId = item.sourceId.replace('-new-task', '');
          const sectionTagId = tagId === untaggedId ? undefined : tagId;
          const contextTagId = groupSettings.tagId;

          const tags = [sectionTagId, contextTagId].filter((t): t is ID => !!t);
          const uniqueTags = Array.from(new Set(tags));

          return (
            <NewTaskRow
              tags={uniqueTags.length ? uniqueTags : undefined}
              startTime={null}
              duration={null}
              goal={groupSettings.goalId}
              lifeArea={groupSettings.lifeAreaId}
            />
          );
        }

        // Tag
        if (type === TreeItemTypes.Tag) {
          return (
            <TagHeader
              item={itemInstance}
              tags={tagsRef.current}
              denseFormat={denseFormat}
            />
          );
        }

        return null;
      },
    [
      denseFormat,
      groupSettings.goalId,
      groupSettings.lifeAreaId,
      groupSettings.displayDue,
      groupSettings.tagId,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={taskTreeItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
